import { createFileRoute } from '@tanstack/react-router';
import { Settings, Sun, Moon, Monitor, Building2, Loader2 } from 'lucide-react';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { useAppSettings } from '~/hooks/use-app-settings';
import { api } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { useTheme } from '~/lib/theme';

export const Route = createFileRoute('/settings/')({
  component: SettingsGeneralPage
});

export default function SettingsGeneralPage() {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const { data: settings, refetch } = useAppSettings();
  const companyName = settings?.companyName ?? 'Open Dataroom';
  const isOwner = user?.role === 'owner';

  const [editValue, setEditValue] = React.useState(companyName);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');

  React.useEffect(() => {
    setEditValue(companyName);
  }, [companyName]);

  const handleSave = async () => {
    setSaveError('');
    setIsSaving(true);
    try {
      await api.patch('/api/settings', { companyName: editValue.trim() });
      await refetch();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Settings className="h-5 w-5" />
        <h2 className="text-2xl font-bold">General</h2>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <CardTitle>Organization</CardTitle>
          </div>
          <CardDescription>
            Customize how your organization appears across the app and in
            emails.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isOwner ? (
            <>
              <div className="space-y-2">
                <label htmlFor="company-name" className="text-sm font-medium">
                  Company name
                </label>
                <Input
                  id="company-name"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="Enter company name"
                  maxLength={100}
                />
              </div>
              {saveError && (
                <p className="text-sm text-destructive">{saveError}</p>
              )}
              <Button
                onClick={handleSave}
                disabled={isSaving || editValue.trim() === companyName}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium">Company name</p>
              <p className="text-sm text-muted-foreground">{companyName}</p>
              <p className="text-xs text-muted-foreground">
                Only owners can change this setting.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Customize the look and feel</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm font-medium">Theme</p>
          <div className="grid grid-cols-3 gap-1.5">
            {(
              [
                { value: 'light' as const, label: 'Light', Icon: Sun },
                { value: 'dark' as const, label: 'Dark', Icon: Moon },
                { value: 'system' as const, label: 'System', Icon: Monitor }
              ] as const
            ).map(({ value: t, label, Icon }) => (
              <button
                key={t}
                type="button"
                onClick={() => setTheme(t)}
                className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium capitalize transition-colors ${
                  theme === t
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-accent'
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
